import { useEffect } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';

import { CLAIM_BOOKING_LOOKUP } from '@/components/ReactivationFlowContainer/schemas/claimBookingLookupSchema';
import {
  ClaimBookingResponse,
  GenerateClaimBookingOTPResult,
  LookupBookingForClaimResult,
  PostClaimSailorProfileUpdateInput,
  PostClaimSailorProfileUpdateResult,
} from '@/components/ReactivationFlowContainer/types/LookupBookingForClaimTypes';
import { logError, logWarn } from '@/helpers/logger/logUtils';

type GenerateOtpMutationResponse = { generateClaimBookingOTP: GenerateClaimBookingOTPResult };
type ClaimBookingLookupMutationResponse = { claimBooking: ClaimBookingResponse };
type LookupData = { lookupBookingForClaim: LookupBookingForClaimResult };
type PostClaimSailorProfileUpdateResponse = { postClaimSailorProfileUpdate: PostClaimSailorProfileUpdateResult };

export const useClaimBookingLookup = () => {
  const [generateClaimBookingOTP, generateOtpState] = useMutation<GenerateOtpMutationResponse>(
    CLAIM_BOOKING_LOOKUP.generateClaimBookingOTP,
  );

  const [claimBookingLookup, claimBookingState] = useMutation<ClaimBookingLookupMutationResponse>(
    CLAIM_BOOKING_LOOKUP.claimBooking,
  );

  const [postClaimSailorProfileUpdate, postClaimProfileState] = useMutation<
    PostClaimSailorProfileUpdateResponse,
    { value: PostClaimSailorProfileUpdateInput }
  >(CLAIM_BOOKING_LOOKUP.postClaimSailorProfileUpdate);

  const [runLookupBookingForClaim, lookupState] = useLazyQuery<LookupData>(CLAIM_BOOKING_LOOKUP.lookupBookingForClaim, {
    fetchPolicy: 'no-cache',
    onError: () => {
      logError('lookup booking for claim failed');
    },
  });

  useEffect(() => {
    if (generateOtpState.error) {
      logWarn('generateClaimBookingOTP mutation failed');
      generateOtpState.error.graphQLErrors?.map((gqlError) => logError(gqlError));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateOtpState.error]);

  useEffect(() => {
    if (claimBookingState.error) {
      logWarn('claimBooking (OTP) mutation failed');
      claimBookingState.error.graphQLErrors?.map((gqlError) => logError(gqlError));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimBookingState.error]);

  useEffect(() => {
    if (postClaimProfileState.error) {
      logWarn('postClaimSailorProfileUpdate mutation failed');
      postClaimProfileState.error.graphQLErrors?.map((gqlError) => logError(gqlError));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postClaimProfileState.error]);

  return {
    generateClaimBookingOTP,
    loadingGenerateOtp: Boolean(generateOtpState.loading),
    claimBookingLookup,
    loadingClaimLookup: Boolean(claimBookingState.loading),
    runLookupBookingForClaim,
    loadingLookup: Boolean(lookupState.loading),
    postClaimSailorProfileUpdate,
    loadingPostClaimProfile: Boolean(postClaimProfileState.loading),
  } as const;
};
